import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API } from '../utils/apiBase';

/**
 * SiteCopyContext — fetches all editable site copy once, caches in memory.
 *
 * Components use `useCopy(key, fallback)` to read a value. The fallback is
 * always the component's existing hardcoded text, so the storefront never
 * breaks during rollout if a key is missing from the DB.
 *
 * After admin saves a change in /admin → Copy, the storefront tabs re-fetch
 * on focus, so updates show up within a few seconds.
 */
const SiteCopyContext = createContext({ copy: {}, refresh: () => {} });

export const SiteCopyProvider = ({ children }) => {
  const [copy, setCopy] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/site-copy`, { cache: 'no-store' });
      if (!r.ok) return;
      const data = await r.json();
      if (data && typeof data === 'object') setCopy(data);
    } catch (e) {
      // Fallbacks will be used; log so failures aren't silently swallowed.
      console.warn('[SiteCopy] fetch failed, using fallbacks:', e);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return (
    <SiteCopyContext.Provider value={{ copy, refresh }}>
      {children}
    </SiteCopyContext.Provider>
  );
};

export const useCopy = (key, fallback = '') => {
  const { copy } = useContext(SiteCopyContext);
  const v = copy[key];
  if (v === undefined || v === null) return fallback;
  return v;
};

export const useCopyRefresh = () => useContext(SiteCopyContext).refresh;
